import { ExtractedLink } from "../gmail/types";
import { extractHostname, extractETldPlusOne } from "../gmail/domainUtils";

export type RiskLevel = "low" | "medium" | "high";

export interface RiskResult {
  score: number;
  level: RiskLevel;
  reasons: string[];
  signals: Record<string, unknown>;
}

export interface ScoringInput {
  from?: string;
  replyTo?: string;
  subject?: string;
  snippet?: string;
  headers?: Record<string, string>;
  links?: ExtractedLink[];
  attachments?: string[];
}

const brandKeywords = ["amazon", "paypal", "apple", "microsoft", "bank", "chase", "wells fargo", "boa"];
const languageSignals = [
  "kindly",
  "urgent action required",
  "verify your account",
  "suspended",
  "limited time",
  "gift cards",
  "crypto",
  "wire transfer"
];

export function scoreEmail(input: ScoringInput): RiskResult {
  let score = 0;
  const reasons: string[] = [];
  const signals: Record<string, unknown> = {};

  const fromDomain = getDomain(input.from);
  const replyDomain = getDomain(input.replyTo);
  if (fromDomain && replyDomain && fromDomain !== replyDomain) {
    score += 40;
    reasons.push("Reply-To domain differs from From domain");
    signals.replyToMismatch = { fromDomain, replyDomain };
  }

  if (input.from) {
    const displayName = input.from.split("<")[0].toLowerCase();
    const domain = fromDomain ?? "";
    for (const brand of brandKeywords) {
      if (displayName.includes(brand) && !domain.includes(brand)) {
        score += 18;
        reasons.push(`Display name references ${brand} but domain does not`);
        break;
      }
    }
  }

  const links = input.links ?? [];
  const suspiciousLinks = links.filter((link) => link.flags.length > 0);
  if (suspiciousLinks.length) {
    score += Math.min(50, suspiciousLinks.length * 10);
    reasons.push("Suspicious link patterns detected");
    signals.suspiciousLinks = suspiciousLinks;
  }

  if (links.some((link) => link.domain && /\d+\.\d+\.\d+\.\d+/.test(link.domain))) {
    score += 10;
    reasons.push("Links pointing to IP addresses");
  }

  if (links.some((link) => link.flags.includes("insecure-link"))) {
    score += 10;
    reasons.push("Links using insecure HTTP");
  }

  const textBlob = `${input.subject ?? ""} ${input.snippet ?? ""}`.toLowerCase();
  const matchedPhrases = languageSignals.filter((phrase) => textBlob.includes(phrase));
  if (matchedPhrases.length) {
    score += 35;
    reasons.push("Urgent/pressure language detected");
    signals.language = matchedPhrases;
  }

  const authResults = input.headers?.["authentication-results"];
  if (authResults) {
    const failedSignals: string[] = [];
    if (/spf=fail/.test(authResults)) failedSignals.push("spf");
    if (/dkim=fail/.test(authResults)) failedSignals.push("dkim");
    if (/dmarc=fail/.test(authResults)) failedSignals.push("dmarc");
    if (failedSignals.length) {
      score += 15;
      reasons.push("Authentication checks failed (SPF/DKIM/DMARC)");
      signals.auth = failedSignals;
    }
  }

  if (input.attachments?.some((name) => isDangerousAttachment(name))) {
    score += 20;
    reasons.push("Potentially dangerous attachment type");
  }

  score = Math.min(100, score);
  let level: RiskLevel = "low";
  if (score >= 70) level = "high";
  else if (score >= 35) level = "medium";

  if (reasons.length === 0) {
    reasons.push("No strong scam indicators detected.");
  }

  return { score, level, reasons, signals };
}

function getDomain(input?: string): string | null {
  if (!input) return null;
  const match = input.match(/@([^>]+)>?$/);
  if (!match) return null;
  return extractETldPlusOne(match[1].toLowerCase());
}

function isDangerousAttachment(name: string): boolean {
  return /(\.html?|\.zip|\.exe|\.docm)$/i.test(name);
}

export function compareVisibleVsActual(linkText: string, href: string): boolean {
  const visibleDomain = extractVisibleDomain(linkText);
  const actualDomain = extractHostname(href);
  if (!visibleDomain || !actualDomain) return false;
  return extractETldPlusOne(visibleDomain) !== extractETldPlusOne(actualDomain);
}

export function extractVisibleDomain(text: string): string | null {
  const match = text.match(/[a-z0-9.-]+\.[a-z]{2,}/i);
  return match ? match[0].toLowerCase() : null;
}
